import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";

export interface MediaUploadResult {
  success: boolean;
  url?: string;
  error?: string;
  fileName?: string;
  fileSize?: number;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export class MediaUploadService {
  private static instance: MediaUploadService;
  private readonly BUCKET_NAME = 'proposalimages';
  private readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  private readonly MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
  private readonly MAX_DOCUMENT_SIZE = 20 * 1024 * 1024; // 20MB
  private readonly ALLOWED_IMAGE_TYPES = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml'
  ];
  private readonly ALLOWED_VIDEO_TYPES = [
    'video/mp4',
    'video/webm',
    'video/ogg',
    'video/avi',
    'video/mov',
    'video/quicktime'
  ];
  private readonly ALLOWED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'text/csv',
    'application/json',
    'application/zip',
    'application/x-zip-compressed'
  ];

  public static getInstance(): MediaUploadService {
    if (!MediaUploadService.instance) {
      MediaUploadService.instance = new MediaUploadService();
    }
    return MediaUploadService.instance;
  }

  /**
   * Upload image file to Supabase storage
   */
  async uploadImage(
    file: File, 
    userId: string,
    onProgress?: (progress: UploadProgress) => void,
    bucketName: string = this.BUCKET_NAME
  ): Promise<MediaUploadResult> {
    try {
      console.log('🖼️ [PROPOSAL MEDIA] Starting image upload:', {
        fileName: file.name,
        fileSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
        fileType: file.type,
        userId: userId,
        bucket: bucketName
      });

      // Validate file
      const validation = this.validateImageFile(file);
      if (!validation.valid) {
        return { success: false, error: validation.error };
      }

      // Generate unique file path
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 15);
      const fileName = `image_${timestamp}_${randomId}.${fileExtension}`;
      const filePath = `proposals/${userId}/images/${fileName}`;

      console.log('📁 Upload path:', filePath);

      // Simulate progress for better UX
      if (onProgress) {
        onProgress({ loaded: 0, total: file.size, percentage: 0 });
      }

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false, // Don't overwrite existing files
        });

      if (error) {
        console.error('❌ Upload error:', error);
        return { 
          success: false, 
          error: `Upload failed: ${error.message}` 
        };
      }

      // Simulate progress completion
      if (onProgress) {
        onProgress({ loaded: file.size, total: file.size, percentage: 100 });
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);

      console.log('✅ [PROPOSAL MEDIA] Image uploaded successfully:', {
        path: data.path,
        url: urlData.publicUrl,
        fileName: fileName,
        fileSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
        bucket: bucketName
      });

      return {
        success: true,
        url: urlData.publicUrl,
        fileName: fileName,
        fileSize: file.size
      };

    } catch (error) {
      console.error('❌ Image upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      };
    }
  }

  /**
   * Upload video file to Supabase storage
   */
  async uploadVideo(
    file: File, 
    userId: string,
    onProgress?: (progress: UploadProgress) => void,
    bucketName: string = this.BUCKET_NAME
  ): Promise<MediaUploadResult> {
    try {
      console.log('🎥 Starting video upload:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      // Validate file
      const validation = this.validateVideoFile(file);
      if (!validation.valid) {
        return { success: false, error: validation.error };
      }

      // Generate unique file path
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'mp4';
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 15);
      const fileName = `video_${timestamp}_${randomId}.${fileExtension}`;
      const filePath = `proposals/${userId}/videos/${fileName}`;

      console.log('📁 Upload path:', filePath);

      // Simulate progress for better UX
      if (onProgress) {
        onProgress({ loaded: 0, total: file.size, percentage: 0 });
      }

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        console.error('❌ Video upload error:', error);
        return { 
          success: false, 
          error: `Upload failed: ${error.message}` 
        };
      }

      // Simulate progress completion
      if (onProgress) {
        onProgress({ loaded: file.size, total: file.size, percentage: 100 });
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);

      console.log('✅ Video uploaded successfully:', {
        path: data.path,
        url: urlData.publicUrl
      });

      return {
        success: true,
        url: urlData.publicUrl,
        fileName: fileName,
        fileSize: file.size
      };

    } catch (error) {
      console.error('❌ Video upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      };
    }
  }

  /**
   * Validate image file
   */
  private validateImageFile(file: File): { valid: boolean; error?: string } {
    if (!file) {
      return { valid: false, error: 'No file provided' };
    }

    if (!this.ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return { 
        valid: false, 
        error: `Invalid file type. Allowed types: ${this.ALLOWED_IMAGE_TYPES.join(', ')}` 
      };
    }

    if (file.size > this.MAX_FILE_SIZE) {
      return { 
        valid: false, 
        error: `File too large. Maximum size: ${this.MAX_FILE_SIZE / (1024 * 1024)}MB` 
      };
    }

    return { valid: true };
  }

  /**
   * Upload file (documents, etc.) to Supabase storage
   */
    async uploadFile(
    file: File, 
    userId: string,
    onProgress?: (progress: UploadProgress) => void,
    bucketName: string = this.BUCKET_NAME
  ): Promise<MediaUploadResult> {
    try {
      console.log('📄 Starting file upload:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      // Validate file
      const validation = this.validateFile(file);
      if (!validation.valid) {
        return { success: false, error: validation.error };
      }

      // Generate unique file path
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'bin';
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 15);
      const fileName = `file_${timestamp}_${randomId}.${fileExtension}`;
      const filePath = `proposals/${userId}/files/${fileName}`;

      console.log('📁 Upload path:', filePath);

      // Simulate progress for better UX
      if (onProgress) {
        onProgress({ loaded: 0, total: file.size, percentage: 0 });
      }

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        console.error('❌ File upload error:', error);
        return {
          success: false,
          error: `Upload failed: ${error.message}`
        };
      }

      // Simulate progress completion
      if (onProgress) {
        onProgress({ loaded: file.size, total: file.size, percentage: 100 });
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);

      console.log('✅ File uploaded successfully:', {
        path: data.path,
        url: urlData.publicUrl
      });

      return {
        success: true,
        url: urlData.publicUrl,
        fileName: fileName,
        fileSize: file.size
      };

    } catch (error) {
      console.error('❌ File upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      };
    }
  }

  /**
   * Validate video file
   */
  private validateVideoFile(file: File): { valid: boolean; error?: string } {
    if (!file) {
      return { valid: false, error: 'No file provided' };
    }

    if (!this.ALLOWED_VIDEO_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file type. Allowed types: ${this.ALLOWED_VIDEO_TYPES.join(', ')}`
      };
    }

    if (file.size > this.MAX_VIDEO_SIZE) {
      return {
        valid: false,
        error: `File too large. Maximum size: ${this.MAX_VIDEO_SIZE / (1024 * 1024)}MB`
      };
    }

    return { valid: true };
  }

  /**
   * Validate general file
   */
  private validateFile(file: File): { valid: boolean; error?: string } {
    if (!file) {
      return { valid: false, error: 'No file provided' };
    }

    if (!this.ALLOWED_FILE_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file type. Allowed types: ${this.ALLOWED_FILE_TYPES.join(', ')}`
      };
    }

    if (file.size > this.MAX_DOCUMENT_SIZE) {
      return {
        valid: false,
        error: `File too large. Maximum size: ${this.MAX_DOCUMENT_SIZE / (1024 * 1024)}MB`
      };
    }

    return { valid: true };
  }

  /**
   * Delete media file from storage
   */
  async deleteMedia(filePath: string, bucketName: string = this.BUCKET_NAME): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.storage
        .from(bucketName)
        .remove([filePath]);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Delete failed'
      };
    }
  }

  /**
   * Get media URL from file path
   */
  getMediaUrl(filePath: string): string {
    const { data } = supabase.storage
      .from(this.BUCKET_NAME)
      .getPublicUrl(filePath);
    
    return data.publicUrl;
  }
}

// Export singleton instance
export const mediaUploadService = MediaUploadService.getInstance();
